// ===========================================
// CONTROL PANEL COLOR SCHEME DEMO
// ===========================================
// Displays multiple control panel layouts with different color schemes
// for comparison and selection
// ===========================================

import { useState } from 'react';

// ===========================================
// COLOR SCHEME DEFINITIONS
// ===========================================

interface ColorScheme {
  name: string;
  description: string;
  colors: {
    hit: string;
    stand: string;
    bet: string;
    split: string;
    double: string;
    surrender: string;
    autobet: string;
    autobetActive: string;
  };
}

const COLOR_SCHEMES: ColorScheme[] = [
  // ============================================
  // GRADIENT SCHEMES (NEW - for PixiJS panel)
  // ============================================
  {
    name: 'Gradient: Sunset Flow',
    description: 'Continuous warm gradient across panel',
    colors: {
      // Top to bottom: red → orange → yellow → purple
      split: 'bg-gradient-to-b from-red-600 to-red-500 text-white',
      double: 'bg-gradient-to-b from-red-500 to-orange-500 text-white',
      hit: 'bg-gradient-to-b from-orange-500 to-amber-400 text-black',
      bet: 'bg-gradient-to-b from-amber-400 to-yellow-400 text-black',
      stand: 'bg-gradient-to-b from-yellow-400 to-purple-500 text-black',
      surrender: 'bg-gradient-to-b from-purple-500 to-purple-700 text-white',
      autobet: 'bg-gradient-to-b from-purple-700 to-fuchsia-700 text-white',
      autobetActive: 'bg-fuchsia-600 text-white',
    },
  },
  {
    name: 'Gradient: Ocean Depth',
    description: 'Cool blues flowing top to bottom',
    colors: {
      split: 'bg-gradient-to-b from-cyan-400 to-cyan-500 text-black',
      double: 'bg-gradient-to-b from-cyan-500 to-blue-500 text-white',
      hit: 'bg-gradient-to-b from-blue-500 to-blue-600 text-white',
      bet: 'bg-gradient-to-b from-blue-600 to-indigo-600 text-white',
      stand: 'bg-gradient-to-b from-indigo-600 to-purple-700 text-white',
      surrender: 'bg-gradient-to-b from-purple-700 to-violet-800 text-purple-200',
      autobet: 'bg-gradient-to-b from-violet-800 to-violet-900 text-violet-300',
      autobetActive: 'bg-violet-600 text-white',
    },
  },
  {
    name: 'Gradient: Fire & Ice',
    description: 'Warm top, cool bottom',
    colors: {
      split: 'bg-gradient-to-b from-red-500 to-orange-500 text-white',
      double: 'bg-gradient-to-b from-orange-500 to-yellow-500 text-black',
      hit: 'bg-gradient-to-b from-yellow-500 to-lime-500 text-black',
      bet: 'bg-gradient-to-b from-lime-500 to-cyan-500 text-black',
      stand: 'bg-gradient-to-b from-cyan-500 to-blue-600 text-white',
      surrender: 'bg-gradient-to-b from-blue-600 to-indigo-700 text-blue-200',
      autobet: 'bg-gradient-to-b from-indigo-700 to-purple-800 text-indigo-200',
      autobetActive: 'bg-purple-600 text-white',
    },
  },
  {
    name: 'Gradient: Individual Warm',
    description: 'Each button has red→orange→yellow gradient',
    colors: {
      split: 'bg-gradient-to-b from-red-500 to-orange-500 text-white',
      double: 'bg-gradient-to-b from-red-500 to-orange-500 text-white',
      hit: 'bg-gradient-to-b from-orange-500 to-yellow-400 text-black',
      bet: 'bg-gradient-to-b from-yellow-400 to-amber-500 text-black',
      stand: 'bg-gradient-to-b from-purple-600 to-blue-600 text-white',
      surrender: 'bg-gradient-to-b from-blue-700 to-purple-700 text-blue-200',
      autobet: 'bg-gradient-to-b from-fuchsia-600 to-purple-700 text-white',
      autobetActive: 'bg-fuchsia-500 text-white',
    },
  },
  {
    name: 'Gradient: Neon Glow',
    description: 'Dark to bright neon per button',
    colors: {
      split: 'bg-gradient-to-b from-pink-700 to-pink-500 text-white',
      double: 'bg-gradient-to-b from-rose-700 to-rose-500 text-white',
      hit: 'bg-gradient-to-b from-orange-700 to-yellow-400 text-black',
      bet: 'bg-gradient-to-b from-amber-600 to-yellow-300 text-black',
      stand: 'bg-gradient-to-b from-indigo-700 to-cyan-400 text-white',
      surrender: 'bg-gradient-to-b from-slate-700 to-slate-500 text-white',
      autobet: 'bg-gradient-to-b from-violet-700 to-fuchsia-500 text-white',
      autobetActive: 'bg-fuchsia-500 text-white',
    },
  },
  {
    name: 'Gradient: Casino Royal',
    description: 'Gold and deep purple flow',
    colors: {
      split: 'bg-gradient-to-b from-yellow-500 to-amber-600 text-black',
      double: 'bg-gradient-to-b from-amber-600 to-orange-600 text-black',
      hit: 'bg-gradient-to-b from-orange-600 to-yellow-500 text-black',
      bet: 'bg-gradient-to-b from-yellow-500 to-amber-400 text-black',
      stand: 'bg-gradient-to-b from-amber-400 to-purple-700 text-black',
      surrender: 'bg-gradient-to-b from-purple-700 to-purple-900 text-purple-200',
      autobet: 'bg-gradient-to-b from-purple-900 to-fuchsia-900 text-purple-300',
      autobetActive: 'bg-fuchsia-700 text-white',
    },
  },
  // ============================================
  // ORIGINAL SCHEMES
  // ============================================
  {
    name: 'Current (Traffic Light)',
    description: 'Green for action, Yellow for bet, Red for stand',
    colors: {
      hit: 'bg-green-500 hover:bg-green-400 text-black',
      stand: 'bg-red-500 hover:bg-red-400 text-white',
      bet: 'bg-yellow-500 hover:bg-yellow-400 text-black',
      split: 'bg-green-500 hover:bg-green-400 text-black',
      double: 'bg-green-500 hover:bg-green-400 text-black',
      surrender: 'bg-gray-200 hover:bg-gray-100 text-gray-800',
      autobet: 'border-2 border-green-500 text-green-400 hover:bg-green-500/20',
      autobetActive: 'bg-green-600 hover:bg-green-500 text-white',
    },
  },
  {
    name: 'Vegas Gold',
    description: 'Luxurious gold and amber tones',
    colors: {
      hit: 'bg-amber-500 hover:bg-amber-400 text-black',
      stand: 'bg-amber-800 hover:bg-amber-700 text-white',
      bet: 'bg-yellow-400 hover:bg-yellow-300 text-black',
      split: 'bg-amber-600 hover:bg-amber-500 text-black',
      double: 'bg-amber-600 hover:bg-amber-500 text-black',
      surrender: 'bg-amber-900 hover:bg-amber-800 text-amber-200',
      autobet: 'border-2 border-amber-500 text-amber-400 hover:bg-amber-500/20',
      autobetActive: 'bg-amber-600 hover:bg-amber-500 text-white',
    },
  },
  {
    name: 'Midnight Blue',
    description: 'Cool blues with cyan accents',
    colors: {
      hit: 'bg-cyan-500 hover:bg-cyan-400 text-black',
      stand: 'bg-blue-700 hover:bg-blue-600 text-white',
      bet: 'bg-sky-400 hover:bg-sky-300 text-black',
      split: 'bg-cyan-600 hover:bg-cyan-500 text-black',
      double: 'bg-cyan-600 hover:bg-cyan-500 text-black',
      surrender: 'bg-slate-500 hover:bg-slate-400 text-white',
      autobet: 'border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/20',
      autobetActive: 'bg-cyan-600 hover:bg-cyan-500 text-white',
    },
  },
  {
    name: 'Royal Purple',
    description: 'Rich purples with violet highlights',
    colors: {
      hit: 'bg-violet-500 hover:bg-violet-400 text-white',
      stand: 'bg-purple-800 hover:bg-purple-700 text-white',
      bet: 'bg-fuchsia-400 hover:bg-fuchsia-300 text-black',
      split: 'bg-violet-600 hover:bg-violet-500 text-white',
      double: 'bg-violet-600 hover:bg-violet-500 text-white',
      surrender: 'bg-purple-900 hover:bg-purple-800 text-purple-200',
      autobet: 'border-2 border-violet-500 text-violet-400 hover:bg-violet-500/20',
      autobetActive: 'bg-violet-600 hover:bg-violet-500 text-white',
    },
  },
  {
    name: 'Neon Nights',
    description: 'Dark with vibrant neon accents',
    colors: {
      hit: 'bg-lime-400 hover:bg-lime-300 text-black',
      stand: 'bg-pink-600 hover:bg-pink-500 text-white',
      bet: 'bg-yellow-300 hover:bg-yellow-200 text-black',
      split: 'bg-lime-500 hover:bg-lime-400 text-black',
      double: 'bg-lime-500 hover:bg-lime-400 text-black',
      surrender: 'bg-gray-700 hover:bg-gray-600 text-gray-300',
      autobet: 'border-2 border-lime-400 text-lime-400 hover:bg-lime-400/20',
      autobetActive: 'bg-lime-500 hover:bg-lime-400 text-black',
    },
  },
  {
    name: 'Classic Casino',
    description: 'Traditional red, black, and gold',
    colors: {
      hit: 'bg-red-600 hover:bg-red-500 text-white',
      stand: 'bg-gray-900 hover:bg-gray-800 text-white',
      bet: 'bg-yellow-500 hover:bg-yellow-400 text-black',
      split: 'bg-red-700 hover:bg-red-600 text-white',
      double: 'bg-red-700 hover:bg-red-600 text-white',
      surrender: 'bg-gray-600 hover:bg-gray-500 text-white',
      autobet: 'border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500/20',
      autobetActive: 'bg-yellow-600 hover:bg-yellow-500 text-black',
    },
  },
  {
    name: 'Ocean Breeze',
    description: 'Teals and aquas with coral accent',
    colors: {
      hit: 'bg-teal-500 hover:bg-teal-400 text-white',
      stand: 'bg-coral-500 hover:bg-coral-400 text-white bg-rose-500 hover:bg-rose-400',
      bet: 'bg-emerald-400 hover:bg-emerald-300 text-black',
      split: 'bg-teal-600 hover:bg-teal-500 text-white',
      double: 'bg-teal-600 hover:bg-teal-500 text-white',
      surrender: 'bg-slate-400 hover:bg-slate-300 text-slate-800',
      autobet: 'border-2 border-teal-500 text-teal-400 hover:bg-teal-500/20',
      autobetActive: 'bg-teal-600 hover:bg-teal-500 text-white',
    },
  },
  {
    name: 'Monochrome',
    description: 'Clean grayscale with white accent',
    colors: {
      hit: 'bg-gray-100 hover:bg-white text-gray-900',
      stand: 'bg-gray-700 hover:bg-gray-600 text-white',
      bet: 'bg-white hover:bg-gray-100 text-gray-900',
      split: 'bg-gray-300 hover:bg-gray-200 text-gray-900',
      double: 'bg-gray-300 hover:bg-gray-200 text-gray-900',
      surrender: 'bg-gray-500 hover:bg-gray-400 text-white',
      autobet: 'border-2 border-gray-400 text-gray-400 hover:bg-gray-400/20',
      autobetActive: 'bg-gray-500 hover:bg-gray-400 text-white',
    },
  },
  {
    name: 'BUSTED! Sunset',
    description: 'Logo gradient: red/orange to magenta',
    colors: {
      hit: 'bg-orange-500 hover:bg-orange-400 text-black',
      stand: 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white',
      bet: 'bg-yellow-400 hover:bg-yellow-300 text-black',
      split: 'bg-red-500 hover:bg-red-400 text-white',
      double: 'bg-red-500 hover:bg-red-400 text-white',
      surrender: 'bg-rose-800 hover:bg-rose-700 text-rose-200',
      autobet: 'border-2 border-orange-500 text-orange-400 hover:bg-orange-500/20',
      autobetActive: 'bg-orange-600 hover:bg-orange-500 text-white',
    },
  },
  {
    name: 'BUSTED! Neon',
    description: 'Neon border colors: pink/purple/cyan',
    colors: {
      hit: 'bg-cyan-400 hover:bg-cyan-300 text-black',
      stand: 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white',
      bet: 'bg-pink-400 hover:bg-pink-300 text-black',
      split: 'bg-violet-500 hover:bg-violet-400 text-white',
      double: 'bg-violet-500 hover:bg-violet-400 text-white',
      surrender: 'bg-purple-900 hover:bg-purple-800 text-purple-300',
      autobet: 'border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400/20',
      autobetActive: 'bg-cyan-500 hover:bg-cyan-400 text-black',
    },
  },
  {
    name: 'BUSTED! Gradient',
    description: 'Full logo spectrum: red → blue',
    colors: {
      hit: 'bg-gradient-to-r from-orange-500 to-yellow-400 hover:from-orange-400 hover:to-yellow-300 text-black',
      stand: 'bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-500 hover:to-blue-400 text-white',
      bet: 'bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-300 hover:to-orange-300 text-black',
      split: 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white',
      double: 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white',
      surrender: 'bg-gradient-to-r from-blue-700 to-purple-700 hover:from-blue-600 hover:to-purple-600 text-blue-200',
      autobet: 'border-2 border-fuchsia-500 text-fuchsia-400 hover:bg-fuchsia-500/20',
      autobetActive: 'bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white',
    },
  },
  {
    name: 'BUSTED! Electric',
    description: 'High contrast neon on dark',
    colors: {
      hit: 'bg-[#00ffff] hover:bg-[#66ffff] text-black',
      stand: 'bg-[#ff0080] hover:bg-[#ff4da6] text-white',
      bet: 'bg-[#ffff00] hover:bg-[#ffff66] text-black',
      split: 'bg-[#8000ff] hover:bg-[#9933ff] text-white',
      double: 'bg-[#8000ff] hover:bg-[#9933ff] text-white',
      surrender: 'bg-[#1a1a2e] hover:bg-[#2a2a4e] text-[#ff0080]',
      autobet: 'border-2 border-[#00ffff] text-[#00ffff] hover:bg-[#00ffff]/20',
      autobetActive: 'bg-[#00ffff] hover:bg-[#66ffff] text-black',
    },
  },
];

// ===========================================
// MINI CONTROL PANEL COMPONENT
// ===========================================

function MiniControlPanel({ scheme, isSelected, onSelect }: {
  scheme: ColorScheme;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={`flex flex-col gap-3 p-4 bg-black/40 rounded-2xl backdrop-blur-sm cursor-pointer transition-all ${
        isSelected ? 'ring-4 ring-casino-gold-500 scale-105' : 'hover:ring-2 hover:ring-white/30'
      }`}
      onClick={onSelect}
    >
      {/* Scheme name */}
      <div className="text-center">
        <h3 className="text-white font-bold text-sm">{scheme.name}</h3>
        <p className="text-gray-400 text-xs">{scheme.description}</p>
      </div>

      {/* Button layout */}
      <div className="flex items-stretch gap-1.5">
        {/* Left stack */}
        <div className="flex flex-col gap-1 w-14 shrink-0">
          <button className={`flex-1 py-1.5 font-bold rounded text-xs ${scheme.colors.split}`}>
            SPLIT
          </button>
          <button className={`flex-1 py-1.5 font-bold rounded text-xs ${scheme.colors.double}`}>
            DBL
          </button>
        </div>

        {/* Center buttons */}
        <div className="flex flex-1 gap-1">
          <button className={`flex-1 py-4 font-bold rounded text-sm ${scheme.colors.hit}`}>
            HIT
          </button>
          <button className={`flex-1 py-4 font-bold rounded text-sm ${scheme.colors.bet}`}>
            BET
          </button>
          <button className={`flex-1 py-4 font-bold rounded text-sm ${scheme.colors.stand}`}>
            STAND
          </button>
        </div>

        {/* Right stack */}
        <div className="flex flex-col gap-1 w-14 shrink-0">
          <button className={`flex-1 py-1.5 font-bold rounded text-xs ${scheme.colors.surrender}`}>
            SURR
          </button>
          <button className={`flex-1 py-1.5 font-bold rounded text-xs ${scheme.colors.autobet}`}>
            AUTO
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// FULL SIZE PREVIEW (HORIZONTAL)
// ===========================================

function FullSizePreview({ scheme }: { scheme: ColorScheme }) {
  return (
    <div className="flex flex-col items-center gap-3 p-4 bg-black rounded-2xl w-full max-w-[600px]">
      {/* Action Buttons - full width with side stacks */}
      <div className="flex items-stretch gap-2 w-full">
        {/* Left stack: Split/Double */}
        <div className="flex flex-col gap-2 w-24 shrink-0">
          <button className={`flex-1 py-3 font-bold rounded-lg text-sm ${scheme.colors.split}`}>
            SPLIT
          </button>
          <button className={`flex-1 py-3 font-bold rounded-lg text-sm ${scheme.colors.double}`}>
            DOUBLE
          </button>
        </div>

        {/* Center: HIT, BET, STAND */}
        <div className="flex flex-1 gap-2 min-w-0">
          <button className={`flex-1 min-w-0 py-6 font-bold rounded-lg text-2xl ${scheme.colors.hit}`}>
            HIT
          </button>
          <button className={`flex-1 min-w-0 py-6 font-bold rounded-lg text-2xl ${scheme.colors.bet}`}>
            BET
          </button>
          <button className={`flex-1 min-w-0 py-6 font-bold rounded-lg text-2xl ${scheme.colors.stand}`}>
            STAND
          </button>
        </div>

        {/* Right stack: Surrender + Autobet */}
        <div className="flex flex-col gap-2 w-24 shrink-0">
          <button className={`flex-1 py-3 font-bold rounded-lg text-sm ${scheme.colors.surrender}`}>
            SURRENDER
          </button>
          <button className={`flex-1 py-3 font-bold rounded-lg text-sm ${scheme.colors.autobet}`}>
            AUTOBET
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// VERTICAL PANEL PREVIEW
// ===========================================

function VerticalPreview({ scheme }: { scheme: ColorScheme }) {
  return (
    <div className="flex flex-col gap-1 p-3 bg-black rounded-2xl w-[130px] h-[400px]">
      {/* SPLIT - small */}
      <button className={`py-3 shrink-0 font-bold rounded-lg text-xs ${scheme.colors.split}`}>
        SPLIT
      </button>
      {/* DOUBLE - small */}
      <button className={`py-3 shrink-0 font-bold rounded-lg text-xs ${scheme.colors.double}`}>
        DOUBLE
      </button>
      {/* HIT - large */}
      <button className={`flex-1 font-bold rounded-lg text-xl ${scheme.colors.hit}`}>
        HIT
      </button>
      {/* BET - large */}
      <button className={`flex-1 font-bold rounded-lg text-xl ${scheme.colors.bet}`}>
        BET
      </button>
      {/* STAND - large */}
      <button className={`flex-1 font-bold rounded-lg text-xl ${scheme.colors.stand}`}>
        STAND
      </button>
      {/* SURRENDER - small */}
      <button className={`py-3 shrink-0 font-bold rounded-lg text-xs ${scheme.colors.surrender}`}>
        SURRENDER
      </button>
      {/* AUTOBET - small */}
      <button className={`py-3 shrink-0 font-bold rounded-lg text-xs ${scheme.colors.autobet}`}>
        AUTOBET
      </button>
    </div>
  );
}

// ===========================================
// MAIN PAGE COMPONENT
// ===========================================

export function ControlPanelColorDemo() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedScheme = COLOR_SCHEMES[selectedIndex];

  return (
    <div className="min-h-screen bg-green-800 p-8 select-none">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Control Panel Color Schemes</h1>
        <p className="text-gray-300">Click a scheme to preview it full size below</p>
      </div>

      {/* Color scheme grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto mb-12">
        {COLOR_SCHEMES.map((scheme, index) => (
          <MiniControlPanel
            key={scheme.name}
            scheme={scheme}
            isSelected={index === selectedIndex}
            onSelect={() => setSelectedIndex(index)}
          />
        ))}
      </div>

      {/* Full size previews - horizontal and vertical */}
      <div className="max-w-5xl mx-auto">
        <h2 className="text-xl font-bold text-white text-center mb-4">
          Full Size Preview: {selectedScheme.name}
        </h2>
        <div className="flex justify-center items-start gap-8 flex-wrap">
          {/* Horizontal preview */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-gray-400 text-sm">Horizontal (Bottom)</span>
            <FullSizePreview scheme={selectedScheme} />
          </div>
          {/* Vertical preview */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-gray-400 text-sm">Vertical (Side)</span>
            <VerticalPreview scheme={selectedScheme} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ControlPanelColorDemo;
